import React from 'react';
import {type Control, useWatch} from 'react-hook-form';
import type {AttributeAttributes} from '@netx/core-asset/models/attribute';
import {getURLFormattedText} from '@netx/core-components/Form';
import {
	ReadOnlyMultiSelectField,
	ReadOnlySelectField,
} from '@netx/core-components/SelectField/components';
import {ReadOnlyTextField} from '@netx/core-components/TextField';
import {compareProps} from '@netx/core-next/utils/react';
import {ATTRIBUTE_MULTIPLE_VALUE_TYPES, AttributeType} from '../../constants';
import formatters from '../../formatters';
import {SYSTEM_ATTRIBUTE_LABELS} from '../../locales';

export interface ReadOnlyAttributeProps {
	attrData: AttributeAttributes;
	disabled?: boolean;
	hideLabel?: boolean;
	loadingValue?: boolean;
	limitHeightToContainer?: boolean;
	multipleValues?: boolean;
	value?: any;
	control: any;
}

const getFormattedValue = (attrData: AttributeAttributes, value: any) => {
	if (ATTRIBUTE_MULTIPLE_VALUE_TYPES.includes(attrData.type)) return value;

	// @ts-ignore
	const formattedValue = formatters[attrData.type || 1](value, attrData);
	if (
		formattedValue == null ||
		formattedValue === '' ||
		typeof formattedValue !== 'string'
	)
		return formattedValue;

	return getURLFormattedText(formattedValue);
};

const ReadOnlyAttributeComponent = ({
	attrData,
	hideLabel,
	value,
	...props
}: ReadOnlyAttributeProps) => {
	const _value = props.control
		? useWatch({
				name: `${attrData.templateid}`,
				control: props.control,
			})
		: undefined;

	const name = attrData.system
		? SYSTEM_ATTRIBUTE_LABELS[attrData.attributeid]
		: attrData.name;

	const sendProps = {
		label: hideLabel ? '' : name,
		value: getFormattedValue(attrData, _value ?? value),
		...props,
	};

	// multiselect, tag, tag(vocab), semantic
	if (ATTRIBUTE_MULTIPLE_VALUE_TYPES.includes(attrData.type)) {
		return <ReadOnlyMultiSelectField {...sendProps} />;
	}
	// select
	if ([AttributeType.SELECT].includes(attrData.type)) {
		return <ReadOnlySelectField {...sendProps} />;
	}
	// text, textarea, number, data
	if (
		[
			AttributeType.TEXT,
			AttributeType.TEXTAREA,
			AttributeType.NUMBER,
			AttributeType.DATE,
		].includes(attrData.type)
	) {
		return <ReadOnlyTextField {...sendProps} />;
	}

	return null;
};

export const ReadOnlyAttribute = React.memo(
	ReadOnlyAttributeComponent,
	compareProps(),
);
export default ReadOnlyAttribute;
